import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, GuardResult, MaybeAsync, Router, RouterStateSnapshot } from '@angular/router';
import { HttpStatusCode } from '@angular/common/http';
import { ToastService } from '@libs/shared-services';
import { authActions, selectIsUserLoggedIn } from '@libs/store';
import { ffdNotesRoutePrefix, routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, take } from 'rxjs';
import { BaaSignRequestResolveService } from '../tokens/baa-sign-request-resolve.service';
import { getFfdNotesInvitationErrorKey } from '../tokens/ffd-notes-invitation-error.util';
import { FfdNotesInvitationResolveService } from '../tokens/ffd-notes-invitation-resolve.service';

// Handles navigation into the BAA sign-up flow via the FFD Notes invitation `code` query param (as
// opposed to a `notesBaaSignRequestToken`). Re-resolves the code so a page reload on a step past the
// welcome screen (where resolveCode$ normally runs) doesn't lose the in-memory resolutionSignal and
// wrongly show the invalid-link toast.
function resolveInvitationCode$(
  code: string,
  invitationResolveService: FfdNotesInvitationResolveService,
  toastService: ToastService,
  translateService: TranslateService,
  router: Router,
): MaybeAsync<GuardResult> {
  if (invitationResolveService.resolutionSignal() && invitationResolveService.codeSignal() === code) {
    return true;
  }

  invitationResolveService.setCode(code);
  return invitationResolveService.resolveCode$().pipe(
    map(() => true),
    catchError((error) => {
      toastService.error(translateService.instant(getFfdNotesInvitationErrorKey(error)));
      return of(router.createUrlTree([...ffdNotesRoutePrefix(), routes.login]));
    }),
  );
}

export const baaRequestTokenGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const resolveService = inject(BaaSignRequestResolveService);
  const invitationResolveService = inject(FfdNotesInvitationResolveService);
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);
  const router = inject(Router);
  const store = inject(Store);

  const token = route.queryParamMap.get('notesBaaSignRequestToken');
  if (!token) {
    const code = route.queryParamMap.get('code');
    if (code) {
      return resolveInvitationCode$(code, invitationResolveService, toastService, translateService, router);
    }

    if (invitationResolveService.resolutionSignal()) {
      return true;
    }

    toastService.error(translateService.instant('baa_request_link_invalid_error'));
    return router.createUrlTree([...ffdNotesRoutePrefix(), routes.login]);
  }

  resolveService.setToken(token);

  // Skip the API call if we already resolved this exact token in the same session
  if (resolveService.resolutionSignal() && resolveService.notesBaaSignRequestTokenSignal() === token) {
    return true;
  }

  return resolveService.resolveToken$().pipe(
    map(() => true),
    catchError((error) => {
      const isConflict = error?.statusCode === HttpStatusCode.Conflict || error?.status === HttpStatusCode.Conflict;
      const isExpired = error?.statusCode === HttpStatusCode.Gone || error?.status === HttpStatusCode.Gone;

      if (isConflict) {
        store.dispatch(authActions.onProfileRequested());
        return store.select(selectIsUserLoggedIn).pipe(
          filter((isLoggedIn): isLoggedIn is boolean => isLoggedIn !== null),
          take(1),
          map(
            (isLoggedIn) =>
              isLoggedIn || router.createUrlTree([...ffdNotesRoutePrefix(), routes.login], { queryParams: { redirectUrl: state.url } }),
          ),
        );
      }

      const key = isExpired ? 'baa_request_link_expired_error' : 'baa_request_link_invalid_error';
      toastService.error(translateService.instant(key));
      return of(router.createUrlTree([...ffdNotesRoutePrefix(), routes.login]));
    }),
  );
};
